import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Send, ChevronDown, ChevronUp, Check, AlertTriangle, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  devis_abandonne:    { label: 'Devis abandonné',    color: '#F97316', emoji: '📋' },
  acompte_en_attente: { label: 'Acompte en attente', color: 'var(--admin-red)', emoji: '💰' },
  evenement_proche:   { label: 'Événement proche',   color: 'var(--admin-purple)', emoji: '⏰' },
};

export default function AdminRelances() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [messages, setMessages] = useState({});
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState(new Set());

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/relances/detecter`, { headers });
      const d = await res.json();
      setData(d);
      // Pré-remplir les messages
      const msgs = {};
      (d.relances || []).forEach(r => { msgs[r.id] = r.message_suggere; });
      setMessages(msgs);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendRelance = async (relance) => {
    if (!relance.client_email) { toast.error('Pas d\'email client'); return; }
    setSending(relance.id);
    try {
      await fetch(`${BACKEND}/api/admin/relances/envoyer`, {
        method: 'POST', headers,
        body: JSON.stringify({
          relance_id: relance.id,
          quote_id: relance.quote_id,
          client_email: relance.client_email,
          client_nom: relance.client_nom,
          message: messages[relance.id] || relance.message_suggere,
          sujet: relance.sujet,
        }),
      });
      toast.success(`Relance envoyée à ${relance.client_nom}`);
      setSent(prev => new Set([...prev, relance.id]));
      setExpanded(null);
    } catch { toast.error('Erreur envoi'); }
    finally { setSending(null); }
  };

  const relances = data?.relances || [];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paiements</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Relances intelligentes</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Suggestions de relance basées sur le comportement</p>
      </div>
      <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.1)' }}>
              <Zap size={20} style={{ color: '#F97316' }} />
            </div>
            <h1 className="text-2xl font-display font-bold" style={{ color: 'var(--admin-text)' }}>Relances Intelligentes</h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Détection automatique des relances à envoyer</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium"
          style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
          <RefreshCw size={14} /></button>
      </div>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: 'Devis abandonnés', value: data.par_type?.devis_abandonne || 0, color: '#F97316' },
            { label: 'Acomptes en attente', value: data.par_type?.acompte_en_attente || 0, color: 'var(--admin-red)' },
            { label: 'Événements proches', value: data.par_type?.evenement_proche || 0, color: 'var(--admin-purple)' },
          ].map((s, i) => (
            <div key={i} className="card-premium p-4 text-center">
              <p className="font-display text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Liste relances */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 rounded-2xl animate-pulse" style={{ background: 'var(--border)' }} />)}</div>
      ) : relances.length === 0 ? (
        <div className="text-center py-12">
          <Check size={36} style={{ color: '#22C55E', margin: '0 auto 12px' }} />
          <p className="font-semibold" style={{ color: 'var(--admin-text)' }}>Aucune relance nécessaire</p>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Tous vos clients sont à jour !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {relances.map(rel => {
            const tc = TYPE_CONFIG[rel.type] || TYPE_CONFIG.devis_abandonne;
            const isOpen = expanded === rel.id;
            const isSent = sent.has(rel.id);

            return (
              <div key={rel.id} className="rounded-2xl overflow-hidden"
                style={{ border: `1px solid ${isSent ? 'rgba(34,197,94,0.3)' : `${tc.color}30`}`, background: '#FFF', opacity: isSent ? 0.7 : 1 }}>
                <button onClick={() => setExpanded(isOpen ? null : rel.id)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                  style={{ cursor: 'pointer', background: 'none', border: 'none' }}>
                  <span className="text-xl flex-shrink-0">{isSent ? '✅' : tc.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{rel.client_nom}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: `${tc.color}15`, color: tc.color }}>{tc.label}</span>
                      {rel.jours > 0 && <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>📅 J+{rel.jours}</span>}
                    </div>
                    <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>
                      {rel.client_email} {rel.total > 0 ? `· ${rel.total}€` : ''}
                    </p>
                  </div>
                  {!isSent && (
                    <button
                      onClick={e => { e.stopPropagation(); sendRelance(rel); }}
                      disabled={sending === rel.id}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold flex-shrink-0"
                      style={{ background: tc.color, color: 'var(--admin-text)', border: 'none', cursor: 'pointer', opacity: sending === rel.id ? 0.7 : 1 }}>
                      <Send size={12} /> {sending === rel.id ? '...' : 'Envoyer'}
                    </button>
                  )}
                  {isOpen ? <ChevronUp size={14} style={{ color: 'var(--admin-muted)', flexShrink: 0 }} /> : <ChevronDown size={14} style={{ color: 'var(--admin-muted)', flexShrink: 0 }} />}
                </button>

                {isOpen && !isSent && (
                  <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--admin-border)' }}>
                    <p className="text-xs font-bold mt-3 mb-1.5" style={{ color: 'var(--admin-text)' }}>Message (modifiable)</p>
                    <textarea
                      rows={6}
                      value={messages[rel.id] || ''}
                      onChange={e => setMessages(prev => ({ ...prev, [rel.id]: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl text-xs resize-none mb-3"
                      style={{ border: '1px solid var(--admin-border)', background: '#FFF', color: 'var(--admin-text)', outline: 'none', lineHeight: '1.6' }}
                    />
                    <button
                      onClick={() => sendRelance(rel)}
                      disabled={sending === rel.id}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
                      style={{ background: tc.color, color: 'var(--admin-text)', border: 'none', cursor: 'pointer', opacity: sending === rel.id ? 0.7 : 1 }}>
                      <Send size={14} /> {sending === rel.id ? 'Envoi...' : `Envoyer à ${rel.client_email}`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
